// English — the SOURCE dictionary. Its shape (`typeof en`) is what types every
// t('key') call across the app (see i18next.d.ts), so adding a key here makes it
// available + type-checked everywhere; the other locales must mirror this shape.
// en is bundled (the fallback); es/fr/de/ja are lazy-loaded on demand.
//
// Interpolation uses i18next's {{param}} syntax: t('home.greeting', { name }).
export default {
    common: {
        expand: 'Expand',
        collapse: 'Collapse',
        close: 'Close',
        loading: 'Loading…',
        backToTop: 'Back to top',
        copy: 'Copy',
        copied: 'Copied',
        discard: 'Discard',
        save: 'Save',
        cancel: 'Cancel',
        edit: 'Edit',
        remove: 'Remove',
    },
    members: {
        tabMembers: 'Members',
        membersHint: 'Everyone in this organization, and the role each one holds. Invite a colleague, or change what someone is.',
        invitationsHint: 'People who have been invited and have not answered yet. An invitation that has been accepted moves to Members.',
        inThisOrg: 'IN THIS ORGANIZATION',
        awaitingReply: 'AWAITING REPLY',
        tabInvitations: 'Invitations',
        searchMembers: 'Search members…',
        searchInvitations: 'Search invitations…',
        invite: 'Invite',
        expiresLabel: 'Expires',
        expiresDays: '{{count}} days',
        invitePlaceholder: 'Email address',
        send: 'Send invite',
        sending: 'Sending…',
        cancel: 'Cancel',
        remove: 'Remove',
        confirmRemove: 'Remove {{name}} from this organization? They lose access immediately.',
        revoke: 'Revoke',
        invitedAs: 'Invited as {{role}}',
        joined: 'Joined {{date}}',
        invitedOn: 'Invited {{date}}',
        noMembers: 'No members match.',
        noInvitations: 'No invitations pending.',
        inviteUnavailable: 'Inviting is unavailable right now.',
        revokeUnavailable: 'This invitation cannot be revoked right now.',
    },
    nav: {
        all: 'All',
        gateways: 'Relays',
        controllers: 'PLCs & CNCs',
        integrations: 'Integrations',
        models: 'Models',
        cameras: 'Cameras',
        dashboards: 'Dashboards',
    },
    dashboards: {
        previewProblems: 'What this widget draws that no widget should: fix these before it goes on a board.',
        share: 'Share',
        shareHeading: 'Share “{{title}}”',
        shareClose: 'Close',
        shareLink: 'Anyone with the link',
        shareLinkHelp: 'No sign-in. View only. Always expires.',
        shareInvite: 'Invite by email',
        shareInviteHelp: 'They sign in as themselves; any account works, no org needed.',
        shareEmails: 'Emails',
        shareExpiry: 'Expires',
        shareUntil: 'Until',
        shareTtl_hour: 'In 1 hour',
        shareTtl_day: 'In 1 day',
        shareTtl_week: 'In 1 week',
        shareTtl_month: 'In 30 days',
        shareTtl_custom: 'Custom…',
        shareInteraction: 'Let them use the board’s buttons',
        shareInteractionHelp: 'Start, Stop and any command a tile carries. Off, the board is view only.',
        shareLinkReadOnly: 'A public link can look, never touch.',
        shareMakeLink: 'Make link',
        shareSendInvite: 'Create invite',
        shareLinkReady: 'Your link. Shown once — copy it now.',
        shareInviteReady: 'Invite created. Send this address to the people you named.',
        shareCopy: 'Copy',
        shareCopied: 'Copied',
        shareLinkOnce: 'The secret is not stored, so this link cannot be shown again. Make a new one if you lose it.',
        alarmsClear: 'All clear',
        alarmsWatched: '{{n}} points watched',
        alarmNear: 'near limit',
        alarmHigh: 'high',
        alarmLow: 'low',
        alarmLimit: 'limit',
        alarmsActiveOf: '{{n}} active of {{total}} watched',
        tileSettings: 'Settings',
        publishWidget: 'Publish widget',
        publishName: 'Name',
        publishScope: 'Who can see it',
        publishOrg: 'This organisation',
        publishGlobal: 'Everyone',
        publishConfirm: 'Publish',
        publishHint: 'Publishing puts this widget on the marketplace as it is now. A later change to it becomes a new widget.',
        settingsSave: 'Save',
        settingsCancel: 'Cancel',
        settingsAddRow: 'Add',
        settingsRemoveRow: 'Remove',
        shareInviteSend: 'They sign in with the invited email and the board opens.',
        shareExisting: 'Active shares',
        shareNone: 'This board is not shared.',
        shareExpires: 'until {{when}}',
        shareCanAct: 'can act',
        shareRevoke: 'Revoke this share',
        shareRevokePerson: 'Remove this person',
        shareExpiryInvalid: 'Pick an expiry between now and 30 days out.',
        shareEmailsNeeded: 'Add at least one email.',
        sharedLive: 'Live',
        sharedViewOnly: 'view only',
        shareGone: 'This share does not exist.',
        shareGoneHelp: 'Ask whoever shared the board for a new link.',
        shareRefused: 'This board cannot be opened.',
        shareExpired: 'This share has expired.',
        shareExpiredHelp: 'Ask whoever shared the board for a new link.',
        back: 'Relay',
        tileCount_one: '{{count}} tile',
        tileCount_other: '{{count}} tiles',
        widgetsStopped_one: '{{count}} widget stopped',
        widgetsStopped_other: '{{count}} widgets stopped',
        noData: 'No data',
        updatedAgo: 'updated {{ago}}',
        latencyBadge: '{{lag}} latency',
        rateBadge: '{{rate}} updates/s',
        empty: 'This dashboard has no tiles yet.',
        notFound: 'That dashboard no longer exists.',
        gatewayOffline: 'This relay is offline, so its dashboards cannot be shown.',
        gatewayConnecting: 'Connecting to the relay…',
        gatewayDisconnected: 'Connect to this relay to see its dashboards.',
        controller: 'controller',
        controllers: 'controllers',
        descriptionFallback: 'No description yet — click to add one, or ask the assistant.',
        descriptionEditHint: 'Click to edit',
        hintExplain: 'Explain this dashboard to me',
        hintImprove: 'How could this dashboard be more useful?',
        hintDescribe: 'Write a description for this dashboard',
        hintBind: 'Bind the {{count}} empty tile(s) on this board',
        board: 'Board',
        activity: 'Activity',
        logs: 'Logs',
        symbol: 'symbol',
        symbols: 'symbols',
        usedBy: 'Used by',
        value: 'Value',
        notReading: 'not reading',
        waiting: 'waiting…',
        noSources: 'No tile on this board reads a symbol.',
        noActivity: 'No changes recorded for this dashboard yet.',
        someone: 'someone',
        statusCreated: 'Created',
        statusEdited: 'Edited',
        create: 'Create dashboard',
        createHeading: 'New dashboard',
        createConfirm: 'Create',
        creating: 'Creating…',
        edit: 'Edit',
        editHeading: 'Edit dashboard',
        editConfirm: 'Save',
        saving: 'Saving…',
        cancel: 'Cancel',
        titleLabel: 'Title',
        titlePlaceholder: 'What this board is called',
        titleNeeded: 'Give the board a title.',
        descriptionLabel: 'Description',
        descriptionPlaceholder: 'What it is for — the assistant reads this too',
        backgroundLabel: 'Background',
        backgroundChoose: 'Choose image…',
        backgroundReplace: 'Replace image…',
        backgroundRemove: 'Remove',
        backgroundNone: 'No background',
        backgroundHint: 'Resized and compressed here before it is saved. Large photographs compress poorly — a texture or a gradient works best.',
        backgroundDim: 'Dim',
        backgroundFit: 'Fit',
        backgroundFitCover: 'Fill the board',
        backgroundFitContain: 'Fit the whole image',
        backgroundFitTile: 'Repeat it',
        createFailed: 'That dashboard could not be created.',
        saveFailed: 'Those changes could not be saved.',
        kiosk: 'Kiosk view',
        kioskExit: 'Exit kiosk',
        live: 'Live',
        stale: 'Stale — no data arriving',
        delete: 'Delete',
        deleteHeading: 'Delete dashboard',
        deleteMessage: 'Delete "{{title}}"?',
        deleteDetail: 'The board and its change history are removed. Widgets and their data are untouched.',
        deleteFailed: 'Could not delete the board.',
        emptyGatewayHint: 'No dashboards on this relay yet. Ask the AI to create one, or import a board file.',
        actionCreated: 'created it',
        actionEdited: 'edited',
        descriptionFallbackShort: 'Open it to see what it shows.',
    },
    worldmap: {
        zoomIn: 'Zoom in',
        zoomOut: 'Zoom out',
        worldView: 'World view',
        dayNight: 'Day / night',
        minimap: 'Click to recenter',
        clickToOpen: 'click to expand',
        cloud: 'Private Server',
        unclaimed: 'Unclaimed',
    },
    marketplace: {
        tabModels: 'Models',
        tabWidgets: 'Dashboard Widgets',
        searchPlaceholder: 'Search the marketplace… type / for commands',
        download: 'Add to a dashboard',
        installHeading: 'Add \u201c{{widget}}\u201d to a dashboard',
        installNoBoards: 'No dashboards yet \u2014 create one first, then add the widget.',
        mapTitle: 'Map',
        official: 'Vitesse',
        officialTitle: 'Shipped by Vitesse: official and safe to install.',
        mapSummary: 'Your fleet on a world map — live health pins, private servers, clustering, plus labelled marker pins.',
        modelsEmpty: 'Models published across orgs will appear here.',
        noResults: 'Nothing matches that search.',
        title: 'Marketplace',
        close: 'Close',
        description: 'Widgets and models shipped by Vitesse or published across orgs. Add one to a dashboard; it comes in vanilla and you bind it to your floor.',
        itemsCount: '{{n}} items',
        officialCount: '{{n}} official',
        by: 'by {{author}}',
        communityCount: '{{n}} community',
        empty: 'Models and widgets published across orgs will appear here.',
    },
    header: {
        toggleTree: 'Toggle symbol tree',
        aiWorking: 'The assistant is working',
        aiFinished: 'The assistant has finished',
        toggleAi: 'Toggle AI panel',
        toggleMarketplace: 'Marketplace',
        closeMarketplace: 'Close marketplace',
        home: 'Home',
        language: 'Language',
        theme: 'Theme',
        search: 'Search…',
        aiShort: 'AI',
        switchSurface: 'Switch app',
        surfaceApp: 'App',
        surfaceAi: 'AI',
        surfaceFleet: 'Fleet',
    },
    theme: {
        auto: 'Auto',
        light: 'Light',
        dark: 'Dark',
    },
    // The tree's group over what spans every relay (Flows, Findings): org-wide pages go here, not
    // to the top of the tree.
    workspace: {
        title: 'Workspace',
    },
    canvas: {
        title: 'Flows',
        description: 'See your data flow across your org, including your hierarchical programs.',
        connections: '{{count}} Connections',
        activePaths: '{{count}} Active Paths',
        offline: '{{count}} offline',
        back: 'Home',
        placeholder: "The program canvas is coming — your org's models wired into programs, live.",
        hintCreate: 'Help me create an SCXML program',
        hintPrograms: 'What are programs vs models?',
    },
    findings: {
        title: 'Findings',
        description: 'What analyses measured, found and proposed, dated and kept. A person accepts, completes or declines each proposal here, with the reason.',
        back: 'Home',
        hintOpen: 'What is still waiting on a decision?',
        hintChanged: 'What changed since the last baseline?',
        hintController: 'What have we found about this controller?',
        tabOpen: 'Open',
        tabDue: 'Due',
        tabAll: 'All',
        search: 'Search claims or signals…',
        allRelays: 'All relays',
        relayFilter: 'Filter by relay',
        allControllers: 'All controllers',
        controllerFilter: 'Filter by controller',
        colId: 'ID',
        colKind: 'Kind',
        colClaim: 'Finding',
        colStatus: 'Status',
        colAbout: 'About',
        colWhen: 'Recorded',
        aboutMore: '{{first}} and {{n}} more',
        kindBaseline: 'Baseline',
        kindChange: 'Change',
        kindAnomaly: 'Anomaly',
        kindProposal: 'Proposal',
        kindDefinition: 'Definition',
        proposed: 'Proposed',
        statusProposed: 'Awaiting decision',
        statusAccepted: 'Accepted',
        statusDone: 'Done',
        statusDeclined: 'Declined',
        emptyOpen: 'Nothing is waiting on a person.',
        emptyDue: 'No follow-ups are due.',
        emptyAll: 'No findings yet. They appear here when an analysis records one.',
        noMatch: 'No findings match.',
        loading: 'Loading findings…',
        loadError: 'Could not read the findings log: {{message}}',
        claim: 'Finding',
        action: 'Action',
        owner: 'Carried out by',
        ownerNone: 'not named',
        followUp: 'Re-measure',
        dueNow: 'due',
        definition: 'Definition',
        follows: 'Follows',
        about: 'About',
        evidence: 'Evidence',
        noEvidence: 'No analysis runs were attached.',
        run: 'Run {{n}}',
        rowsUnknown: 'rows unknown',
        produced: 'Produced',
        code: 'Code',
        history: 'History',
        reading: 'Reading…',
        readError: 'Could not read this entry.',
        nothingToDecide: 'Nothing to decide: it proposes no action.',
        byAgentFor: 'an agent for {{who}}',
        you: 'you',
        basis: 'Basis',
        measured: 'measured',
        derived: 'derived',
        modelled: 'modelled',
        confidenceLow: 'low confidence',
        confidenceMedium: 'medium confidence',
        confidenceHigh: 'high confidence',
        recorded: 'Recorded for',
        accept: 'Accept',
        complete: 'Mark done',
        decline: 'Decline',
        acceptHeading: 'Accept {{id}}',
        completeHeading: 'Mark {{id}} done',
        declineHeading: 'Decline {{id}}',
        acceptHint: 'It shows as accepted until someone marks it done.',
        completeHint: 'It closes, and its follow-up stops being due.',
        declineHint: 'It closes without being carried out.',
        reason: 'Reason',
        reasonPlaceholder: 'Why — the log keeps it, with your name.',
        cancel: 'Cancel',
        saving: 'Saving…',
    },
    flow: {
        transforms: 'Transforms',
        outputs: 'Outputs',
        noSources: 'No controllers connected yet.',
        noTransforms: 'No models yet.',
        databases: 'Databases',
        zoomIn: 'Zoom in',
        zoomOut: 'Zoom out',
        fitView: 'Fit to view',
    },
    home: {
        greeting: 'Hi, {{name}}!',
        greetingNoName: 'Hi there!',
        subtitle: 'How can I help you today?',
        promptPlaceholder: 'Ask AI anything about your relays…',
        voiceTitle: 'Start voice conversation',
        emptyTab: 'No {{tab}} yet.',
        emptyTabOffline: '{{count}} {{tab}} are on relays that are not connected: {{relays}}.',
        emptyAll: 'Nothing yet.',
        chipGateways: 'Show me connected relays',
        chipModel: 'Help me build a model',
        chipSymbols: 'Read some PLC symbols',
        chipCapabilities: 'What can you do?',
    },
    gateway: {
        type: 'Vitesse Controller',
        connect: 'Connect',
        disconnect: 'Disconnect',
        cancel: 'Cancel',
        connectingHint: 'Connecting to the relay…',
        offlineHint: 'This relay is powered off or unreachable. It will reconnect on its own when it comes back.',
        disconnectedHint: 'Disconnected. Connect to view this relay’s controllers and models.',
        connected: 'Relay connected',
        disconnected: 'Disconnected',
        plcCount: '{{count}} PLCs',
        viaLan: 'LAN',
        viaCloud: 'Private Server',
        back: 'Relays',
        noPlcs: 'No PLCs discovered yet.',
        emptyTab: 'No {{tab}} yet.',
        ariaBack: 'Back to Relays',
        hintSummary: 'Summarize this relay and its PLCs',
        hintIssues: 'Are any controllers having issues?',
    },
    camera: {
        // connection badges (the live media state — replaces the on-feed overlay)
        statusConnected: 'Connected',
        statusConnecting: 'Connecting',
        statusReconnecting: 'Reconnecting',
        statusError: 'Error',
        statusIdle: 'Idle',
        // feed overlays (centered, over the frozen frame)
        overlayConnecting: 'Connecting…',
        overlayReconnecting: 'Reconnecting…',
        overlayUnavailable: 'Unavailable — retrying…',
        overlayPaused: 'Paused',
        // recording toggle (the action the button performs)
        enableRecording: 'Enable recording',
        disableRecording: 'Disable recording',
        // sub-view tabs
        tabLive: 'Live',
        tabRecording: 'Recording',
        tabProperties: 'Properties',
        back: 'Relay',
        ariaBack: 'Back to relay',
        // recording playback
        recordingWindow: 'Last 24 hours',
        recordingEmpty: 'No footage recorded in this window.',
        recordingOff: 'Recording is off for this camera. Enable it to capture footage.',
        recordingLoading: 'Loading…',
        recordingError: 'Could not load this segment.',
        recordingPlay: 'Play',
        recordingDownload: 'Download',
        recordingSelectHint: 'Select a segment to play.',
        recordingSegmentAria: 'Recorded segment at {{time}}',
        // DVR transport (custom player controls; scrub shows a live keyframe preview)
        play: 'Play',
        pause: 'Pause',
        live: 'LIVE',
        jumpLive: 'Jump to live',
        fullscreen: 'Fullscreen',
        // recording control panel (LED storage + buffer mode) + header buttons
        storage: 'Storage',
        recorded: 'Recorded',
        bufferMode: 'Buffer',
        bufferRolling: 'Rolling',
        bufferStop: 'Stop when full',
        pauseRecording: 'Pause recording',
        resumeRecording: 'Resume recording',
        downloadRecording: 'Download recording',
        // AI hints (per active tab)
        hintLiveShowing: 'What is this camera showing?',
        hintLiveDropped: 'Has this feed dropped recently?',
        hintRecordingGaps: 'Are there gaps in this recording?',
        hintRecordingMotion: 'When was there activity on this camera?',
        hintPropsConfig: "What is this camera's configuration?",
    },
    controller: {
        statusBadge: 'Controller {{status}}',
        symbolCount: '{{n}} symbols',
        recordingFor: 'Recording for {{duration}}',
        // AI hints — shown per ACTIVE tab (symbols / program / logs / properties).
        hintPriority: 'How can I make a symbol a priority?',
        hintSymbolsLive: 'Which symbols are changing right now?',
        hintExplain: 'Explain this program to me',
        hintProgramWalk: 'Walk me through this block',
        hintLogsRecent: 'Summarize the recent activity',
        hintLogsError: 'Why did this controller report an error?',
        hintPropsConfig: "What is this controller's configuration?",
        hintPropsCheck: 'Is anything misconfigured?',
        toggleProgramTree: 'Toggle program tree',
        cmdType: 'Filter by block type',
        cmdName: 'Filter by block name',
        back: 'Relay',
        ariaBack: 'Back to relay',
        // Description = per-controller user_data; fallback shows when none is set.
        descriptionFallback: 'PLC connection and symbol management',
        descriptionEditHint: 'Click to edit description',
        descriptionWriteFailed: 'Description write failed',
        // sub-view tabs (Symbols / History / Properties / Program / Logs)
        tabSymbols: 'Symbols',
        tabHistory: 'History',
        tabProperties: 'Properties',
        tabProgram: 'Program',
        tabLogs: 'Logs',
        historyEmpty: 'History is unavailable.',
        historyLoading: 'Loading history…',
        // History tab — timeline controls
        historyRange15m: '15m',
        historyRange1h: '1h',
        historyRange1d: '1d',
        historyRange1w: '1w',
        historyRange1mo: '1mo',
        historyRange1y: '1y',
        historyRangeBeyondLifespan: "Longer than this device's recorded history ({{lifespan}})",
        historyCustomRange: 'Custom…',
        historyCustomTitle: 'Absolute range',
        historyCustomFrom: 'From',
        historyCustomTo: 'To',
        historyApply: 'Apply',
        historyLoadFailed: 'History query failed — retrying…',
        historyNoData: 'No recorded data in this range.',
        historyDataBegins: 'Data begins {{date}}',
        historyExport: 'Export CSV',
        historyExportTitle: 'Export history as CSV',
        historyExportStart: 'Download',
        historyExportRunning: 'Exporting…',
        historyExportFailed: 'Export failed: {{reason}}',
        historyExportSucceeded: 'Download succeeded',
        historyExportBadRange: 'The "to" time must be after the "from" time.',
        // History tab — lane assembly (multi-symbol timeline)
        historyAddSymbol: 'Add symbol',
        historyMaxSymbols: 'Up to {{max}} symbols',
        historyPickerTitle: 'Add a symbol',
        historyNoSymbols: 'Add a symbol to explore its history.',
        // History tab — saved symbol combinations (named lane sets)
        historyCombos: 'Saved sets',
        historyCombosTitle: 'Saved symbol sets',
        historyComboEditTitle: 'Edit symbol set',
        historyComboNewTitle: 'Edit New Set',
        historyComboNew: 'New set',
        historyComboName: 'Name of Set',
        historyComboRequirementName: 'Name of Set',
        historyComboRequirementSymbol: 'At least one symbol',
        historyComboApply: 'Apply this set',
        historyComboSave: 'Save',
        historyComboDelete: 'Delete',
        historyComboRemoveSymbol: 'Remove symbol',
        historyComboDeleteHeading: 'Delete symbol set',
        historyComboDeleteMessage: 'Delete "{{name}}"? This removes it for everyone in your organization.',
        historyComboEmpty: 'No saved sets yet. Save up to three symbols under a name to recall them in one click.',
        historyComboNameRequired: 'A name is required.',
        historyComboNeedSymbol: 'Add at least one symbol.',
        historyComboLoadFailed: 'Could not load saved sets: {{reason}}',
        historyComboSaveFailed: 'Save failed: {{reason}}',
        // Properties tab — PLC metadata rows
        propDriver: 'Driver',
        propAddress: 'Address',
        propModel: 'Model',
        propFirmware: 'Firmware',
        propStatus: 'Status',
        propDiscovery: 'Discovery',
        propSymbols: 'Symbols',
        propError: 'Error',
        propUnknown: 'Unknown',
        // Program tab — code viewer states
        programLoading: 'Loading program…',
        programEmpty: 'No program extracted for this controller yet.',
        programBlocks: '{{n}} blocks',
        programSelectBlock: 'Select a block to view its source.',
        programSourceError: 'Could not load this block’s source.',
        // Logs tab
        logsEmpty: 'Logs coming soon.',
        noLogs: 'No log lines yet — they stream live as the controller runs.',
    },
    integration: {
        // Detail-page chrome — back · Paths | Properties | Logs · header actions.
        back: 'Relay',
        ariaBack: 'Back to relay',
        tabPaths: 'Paths',
        tabProperties: 'Properties',
        tabLogs: 'Logs',
        itemsCount: '{{n}} items',
        edit: 'Edit',
        delete: 'Delete',
        loading: 'Loading…',
        // AI hints — shown per ACTIVE tab (paths / properties / logs).
        hintPathsExpose: 'What data does this integration expose?',
        hintPathsFind: 'Find a table or field by name',
        hintPropsConfig: 'Is this integration configured correctly?',
        hintPropsConnects: 'What does it connect to?',
        hintLogsRecent: 'Summarize this integration’s recent activity',
        hintLogsErrors: 'Were there any connection errors?',
        // Delete confirmation
        deleteHeading: 'Delete integration',
        deleteMessage: 'Delete "{{name}}"?',
        deleteDetail: 'The connection is torn down and its discovered paths are removed. You can add it again.',
        deleteConfirm: 'Delete',
        deleteFailed: 'Could not delete the integration.',
        // Properties tab — the non-secret connection fields.
        propsConnection: 'Connection',
        // Table viewport — the WHERE-chip row filter (server-side pushdown).
        filterAdd: 'filter',
        filterApply: 'Apply',
        filterValue: 'value',
        filterRemove: 'Remove this filter',
        filterOpContains: 'contains',
        filterOpIsNull: 'is null',
        filterOpNotNull: 'is not null',
        filterRows: '{{n}} matching rows',
        // Endpoint (REST) operations — hand-added ops + spec rediscovery.
        opsManage: 'Operations',
        opsAdd: 'Add operation',
        opsRediscover: 'Rediscover',
        opsRediscovering: 'Rediscovering…',
        opsName: 'Name',
        opsMethod: 'Method',
        opsPath: 'Path',
        opsPathHint: 'Use {placeholder} for parameters, e.g. /users/{id}',
        opsNone: 'No hand-added operations yet. Operations found in the OpenAPI spec are listed automatically.',
        opsRemove: 'Remove',
        opsSave: 'Save',
        opsSaving: 'Saving…',
        opsInvalid: 'A name and a path are required.',
        opsCount: '{{n}} operations',
        subsManage: 'Subscriptions',
        subsAdd: 'Add subscription',
        subsName: 'Topic',
        subsSubject: 'Subject (optional)',
        subsHint: 'Leave the subject blank to watch the topic named above. Fill it in to give a long topic a short name. Wildcards are not allowed here — a subscription is permanent; use the discovery scope to narrow what is found.',
        subsNone: 'No hand-added topics yet. Discovery finds topics that are publishing; add one here to watch a topic that is quiet or only reports occasionally.',
        subsRemove: 'Remove',
        subsSaving: 'Saving…',
        subsInvalid: 'A topic is required.',
        subsWildcard: 'Wildcards are not allowed in a subscription.',
        subsSameAsName: 'same as topic',
        scopeLabel: 'Scope: {{scope}}',
        scopeAll: 'whole broker',
        scopeHint: 'What discovery listens to. Change it under Edit — this is not the row filter.',
        pathsCount: '{{n}} paths',
        // Shown beside the toolbar when the caller may browse this integration's
        // paths but not read its values — the tooltip carries the session's reason.
        valuesLocked: 'Live values hidden',
        filterSave: 'Save filter',
        filterSaveName: 'Name this filter',
        filterSaveHint: 'Keep this filter under a name and reopen it later (this browser only)',
        filterSavedHint: 'Saved filter — {{n}} condition(s). Click to apply.',
        filterSavedRemove: 'Forget this saved filter',
        // Table viewport — in-cell editing (a write to the source, not a form).
        editHint: 'Double-click to edit',
        editRefused: 'Edit refused',
        editUnaddressable: 'This row cannot be identified, so it cannot be edited safely. It may be missing a value in a key column.',
        editReadOnly: 'This table has no single row identity, so its cells cannot be edited.',
        tableRefresh: 'Refresh',
        tableRefreshHint: 'Read the rows of this table again',
    },
    // The add / edit integration dialog — its OWN chrome. The connection form
    // inside it is described by the integrations service (labels arrive as
    // English strings); those are translated by key in integrationField below.
    integrationDialog: {
        addHeading: 'Add integration',
        editHeading: 'Edit integration',
        loadingTypes: 'Loading integration types…',
        noTypes: 'No integration types available on this relay.',
        typeLabel: 'Integration type',
        nameLabel: 'Name',
        namePlaceholder: 'Integration name',
        defaultName: 'My Integration',
        keepSecretHint: 'Leave a secret field blank to keep the current value.',
        cancel: 'Cancel',
        test: 'Test',
        testing: 'Testing…',
        add: 'Add',
        save: 'Save',
        adding: 'Adding…',
        saving: 'Saving…',
        testingConnection: 'Testing connection…',
        testOk: 'Test successful',
        testFailed: 'Test failed',
        addFailed: 'Could not add integration',
    },
    // The connection form's section headings, keyed by the catalog's group name.
    integrationGroup: {
        connection: 'Connection',
        auth: 'Auth',
        tls: 'TLS',
        discovery: 'Discovery',
        load: 'Load',
    },
    // Connector field labels, keyed by the catalog FIELD KEY. The service sends an
    // English label with every field; the form substitutes the translation ONLY
    // when the server's label equals the English here, so a connector that labels
    // `host` differently ("Host (nsqd)") keeps its own wording rather than being
    // silently relabelled. Every key in internal/service/catalog.go is listed; the
    // English is the catalog's primary label for that key, verbatim.
    integrationField: {
        access_key: 'Access key',
        account: 'Account identifier',
        account_key: 'Account key',
        admin_port: 'Admin HTTP port (discovery)',
        admin_url: 'Admin URL (override)',
        auth: 'Auth',
        auth_header: 'Auth header name',
        auth_value: 'Auth header value',
        base_url: 'Base URL',
        broker_url: 'Broker URL (override)',
        brokers: 'Bootstrap brokers',
        browse: 'Browse on demand',
        bucket: 'Bucket',
        catalog: 'Catalog',
        cert_file: 'Client certificate file (on the relay)',
        client_id: 'Client ID',
        connection_string: 'Connection string',
        credentials_json: 'Service-account JSON',
        database: 'Database',
        datapoints: 'Datapoints',
        db: 'Database',
        dbname: 'Database',
        discover_filter: 'Discovery scope',
        discover_window_ms: 'Discovery window (ms)',
        encrypt: 'Encrypt',
        endpoint: 'Endpoint (override)',
        host: 'Host',
        http_port: 'HTTP port (discovery)',
        instance: 'Instance',
        interval_ms: 'Update interval (ms)',
        key_file: 'Client key file (on the relay)',
        keyspace: 'Keyspace',
        mgmt_port: 'Management port (discovery)',
        namespace: 'Namespace',
        oauth_audience: 'OAuth audience',
        oauth_auth_style: 'OAuth client authentication',
        oauth_client_id: 'OAuth client ID',
        oauth_client_secret: 'OAuth client secret',
        oauth_scopes: 'OAuth scopes',
        oauth_token_url: 'OAuth token URL',
        openapi_url: 'OpenAPI/Swagger URL',
        org: 'Organization',
        password: 'Password',
        path: 'Database file',
        port: 'Port',
        prefix: 'Key prefix',
        project: 'Project',
        protocol: 'Protocol (override)',
        region: 'Region',
        role: 'Role',
        schema: 'Schema',
        secret_key: 'Secret key',
        security_mode: 'Security mode',
        security_policy: 'Security policy',
        service: 'Service name',
        service_url: 'Service URL (override)',
        sslmode: 'SSL Mode',
        tcp_port: 'TCP port',
        tenant: 'Tenant',
        tls: 'TLS',
        tls_ca_cert: 'CA certificate (PEM)',
        tls_client_cert: 'Client certificate (PEM)',
        tls_client_key: 'Client key (PEM, unencrypted)',
        tls_server_name: 'Certificate name override',
        token: 'Token',
        uri: 'Connection URI (override)',
        url: 'URL (override)',
        user: 'User',
        username: 'Username',
        vhost: 'Virtual host',
        warehouse: 'Warehouse',
    },
    model: {
        simulate: 'Simulate',
        simulateHint: 'Test this model on past data. Nothing is sent to the machine.',
        simulateTitle: 'Simulation',
        simulateWindow: 'Window',
        // Where each bound device's recordings are read from — a card that folds to one
        // line of chips after Find runs; see renderAgainst. The count and the status sit
        // beside the heading.
        simulateAgainstHeading: 'Read history from',
        simulateAgainstCount_one: '{{count}} binding',
        simulateAgainstCount_other: '{{count}} bindings',
        simulateAgainstAllBound: 'all as bound',
        simulateAgainstSwapped_one: '{{count}} controller swapped',
        simulateAgainstSwapped_other: '{{count}} controllers swapped',
        // Column headers: the bound device, the symbols read from it and the inputs they
        // feed, and the gateway controller whose recordings stand in for it.
        simulateAgainstColBinding: 'Binding',
        simulateAgainstColSymbol: 'Symbol → variable',
        simulateAgainstColSource: 'Controller to read from',
        // The picker's entry for the device the model is actually bound to.
        simulateAgainstAsBound: '{{name}} (as bound)',
        // Folds a device's bindings past the first two; the count is the hidden ones.
        simulateAgainstMore: 'Show {{count}} more',
        simulateAgainstLess: 'Show less',
        simulateRun: 'Run',
        simulateRunning: 'Running…',
        replayQuickLast15m: 'the last 15 minutes',
        replayQuickLastHour: 'the last hour',
        replayQuickLastDay: 'the last day',
        replayFindReplayLastHour: 'Replay the last hour',
        replayFindButton: 'Find activity',
        replayFindSearching: 'Searching… {{percent}}%',
        replayFindStop: 'Stop',
        replayFindNone: 'Nothing happened from {{from}} to {{to}}. It stayed in {{state}} the whole time.',
        replayFindMore: 'Only the first {{count}} periods are listed.',
        replayFindStopped: 'Stopped early, so the list only covers the search up to that point.',
        replayFindHung: 'The search stopped responding partway through, so later activity is missing.',
        // Where the time went: waiting on history versus running the model. Seconds, one decimal.
        replayFindTiming: 'Read {{rows}} readings in {{fetch}} s and ran them in {{engine}} s, over {{calls}} history requests.',
        // The progress line once the historian has said how much there is to read.
        replayFindSearchingRows: 'Searching… {{percent}}% of about {{rows}} readings',
        // The find-activity controls: how far back, and which periods to keep. Said before the search runs.
        replayFindSpanLabel: 'over',
        replayFindSpan1d: 'the last day',
        replayFindSpan3d: 'the last 3 days',
        replayFindSpan7d: 'the last 7 days',
        replayFindChipWrites: 'Would change the machine',
        replayFindChipErrors: 'Hit a script error',
        replayFindClear: 'Clear',
        replaySourceTitle: 'What do you want to replay?',
        replaySourceResult: 'A search result',
        replaySourceRange: 'A time range',
        replaySourceResultNone: 'Press Find activity to list the periods this model acted in.',
        replayRangeMeta: 'Any stretch of history — no search needed',
        replayRangeLabel: 'Time range',
        replayRangeMatches_one: 'contains 1 search match',
        replayRangeMatches_other: 'contains {{count}} search matches',
        replayFindMeta_one: '{{count}} of {{total}} matched · {{from}} – {{to}}',
        replayFindMeta_other: '{{count}} of {{total}} matched · {{from}} – {{to}}',
        replayFindMetaAll_one: '1 period · {{from}} – {{to}}',
        replayFindMetaAll_other: '{{count}} periods · {{from}} – {{to}}',
        replayColPick: 'Pick',
        replayColWhen: 'When',
        replayColLength: 'Length',
        replayColMatched: 'Matched on',
        replayColStates: 'States',
        replayColWrites: 'Machine writes',
        replayMatchedEntered: 'entered {{state}}',
        replayMatchedWrites: 'changed the machine',
        replayMatchedErrors: 'hit a script error',
        replayMatchedAny: 'any activity',
        replayStatesVisited: '{{n}} visited',
        replayErrorsShort_one: '· {{count}} error',
        replayErrorsShort_other: '· {{count}} errors',
        replayResultsMore: 'Show {{count}} more',
        replayResultsFewer: 'Show fewer',
        replayPickLabel: 'Search result {{n}} of {{total}}',
        replayMachineWrites_one: '{{count}} machine write',
        replayMachineWrites_other: '{{count}} machine writes',
        replayInclude: 'include',
        replayIncludeExact: 'just the period',
        replayIncludeAround: '±{{minutes}} min around it',
        replayFindYourSearch: 'Your search',
        replayFindStates: 'States',
        replayFindMachine: 'Machine',
        replayFindAddState: 'Add a state…',
        replayFindOptionUnreachable: '{{state}} (can’t be reached)',
        /*
         * THE MODEL IS THE SUBJECT, AND IT ONLY EVER "WOULD HAVE".
         *
         * "Find periods that went to hot" says the PERIOD went to hot, and says it
         * in the plain past, as though the state were something the recordings
         * contain. Nothing here happened: the model was never running, and these
         * are the periods it would have acted in had it been. Both faults are fixed
         * by making the model the subject, which is what lets every clause stay
         * conditional without reading oddly.
         */
        replayFindDescAll: 'Find every period where this model would have acted.',
        replayFindDesc: 'Find periods where this model {{what}}.',
        replayFindDescStates: 'would have entered {{states}}',
        replayFindDescWrites: 'would have changed a value on the machine',
        // When the document names all of its write targets literally, say which.
        replayFindDescWritesTo: 'would have written to {{paths}}',
        replayFindDescErrors: 'would have hit a script error',
        replayFindNoneMatched_one: 'Nothing matched from {{from}} to {{to}}. The model acted once in that time, but not in the way chosen.',
        replayFindNoneMatched_other: 'Nothing matched from {{from}} to {{to}}. The model acted in {{count}} other periods.',
        replayFindShowAll_one: 'Show that period',
        replayFindShowAll_other: 'Show all {{count}}',
        replayFindAllUnreachable: 'None of the chosen states can be reached by a replay, so there is nothing to search for. The check above says why.',
        replayCheckAllReachable: 'A replay can reach every state in this model.',
        replayCheckHeading: 'A replay can’t test all of this model:',
        replayCheckStateWaits: 'can’t be reached. The way in waits for “{{event}}”,',
        replayCheckStateFrom: 'can’t be reached. It is only entered from {{states}}, which can’t be reached either.',
        replayCheckStateNoWayIn: 'can’t be reached. Nothing leads to it.',
        replayCheckEdgeWaits: 'never happens. It waits for “{{event}}”,',
        replayCheckWhyOutside: 'which a replay never sends.',
        replayCheckWhyApp: 'which only comes from the app’s pause, resume, reset or set-variable controls.',
        replayCheckWhyAi: 'which only comes from an AI call finishing, and a replay makes no AI calls.',
        replayCheckWhyNoController: 'which is only sent for controller values, and this model reads none.',
        replayCheckWhyNoModel: 'which is only sent for other models’ outputs, and this model reads none.',
        replayCheckSuggest: 'Did you mean “{{event}}”?',
        simulateStale: 'The controls changed — press Run to replay with them.',
        simulateNoRows: 'The historian recorded nothing for {{symbols}} in this window, so the model had no event to react to and could not move. Try a wider window, or a controller that has this data.',
        simulateCard: 'Simulation',
        simulateGone: 'The replay itself is kept only while this tab is open, so the picture is gone; the summary above is what it found.',
        simulateNoSource: 'The model’s SCXML has not loaded yet.',
        simulateFailed: 'The replay could not run: {{reason}}',
        simulateCheckRefused_one: 'Not replayed: the model’s SCXML has 1 problem that stops it from running correctly. Ask the assistant to fix it, then press Run again.',
        simulateCheckRefused_other: 'Not replayed: the model’s SCXML has {{count}} problems that stop it from running correctly. Ask the assistant to fix them, then press Run again.',
        simulateFindingLine: 'Line {{line}}',
        simulateFindingsMore: '… and {{n}} more',
        simulateSidecarButton: 'Simulate',
        simulateSidecarHint: 'Let the AI write a stand-in for the plant, then run this model against it past the point where it first acts.',
        simulateSidecarNoAi: 'The AI plane is not connected, so no sidecar can be written.',
        simulateSidecarStageFacts: 'Measuring the recording…',
        simulateSidecarStageAsking: 'Writing the plant’s physics…',
        simulateSidecarStageCompiling: 'Checking the sidecar…',
        simulateSidecarStageRunning: 'Simulating…',
        simulateSidecarHeading: 'Sidecar',
        simulateSidecarDraws: 'draws the green line',
        simulateSidecarIntro: 'The AI’s stand-in for your machine: from the moment your model first acts, it decides what the machine does.',
        simulateSidecarIntroGuessed: 'The boxes are numbers it had to guess, because the recording never showed them.',
        simulateSidecarWrittenBy: 'Written by {{model}}',
        simulateSidecarReusedShort: 'reused from an earlier run',
        simulateSidecarWhy: 'Why these values?',
        simulateSidecarAssumedTag: 'assumed',
        simulateSidecarAllMeasured: 'Every number in it was measured from the recording.',
        simulateSidecarNotRead: 'Simulated but not read by the model: {{symbols}}.',
        simulateSidecarShadowGood: 'Before your model did anything, this stand-in matched the real {{symbol}} to within {{error}}, which is a good sign.',
        simulateSidecarShadowPoor: 'Before your model did anything, this stand-in already disagreed with the real {{symbol}} by up to {{error}}, so treat its green line as a rough guess.',
        simulateSidecarShadowShift: 'Before your model did anything, this stand-in’s {{symbol}} changed within {{seconds}} s of when the real one did.',
        simulateSidecarShadowNone: 'Nothing was recorded before your model first acted, so there was nothing to check this stand-in’s {{symbol}} against.',
        simulateSidecarNoDivergence: 'The simulation is the recording: in this window the model never wrote to a command the sidecar responds to, so nothing diverged.',
        simulateSidecarNoWrites: 'This model never wrote to the machine in this window, so there is nothing for a sidecar to respond to and nothing to simulate. The AI was not asked.',
        simulateSidecarRegenerate: 'Regenerate',
        simulateSidecarGateFailed: 'The AI’s sidecar was refused twice. The last attempt and the reason are below.',
        simulateSidecarFailed: 'The simulation could not run: {{reason}}',
        simulateSidecarRemedy: 'What to do: {{remedy}}.',
        simulateSidecarStale: 'The model or its bindings changed — press Simulate to write a new sidecar.',
        simulateSidecarElapsed: '{{seconds}} s',
        simulateSidecarCancelled: 'Cancelled — the AI call was stopped and nothing was written. Press Simulate to start again.',
        replayPlay: 'Play',
        replayPause: 'Pause',
        replayStepBack: 'Previous change',
        replayStepForward: 'Next change',
        replayToStart: 'Jump to the start',
        replayToEnd: 'Jump to the end',
        replaySpeed: 'Speed',
        replaySpeedMax: 'as fast as possible',
        replaySpeedHint: 'At {{speed}}, the clock on the right moves {{covers}} for every second you watch, so this whole window takes {{total}} to play.',
        replaySpeedHintMax: 'As fast as possible skips the quiet stretches: the clock on the right jumps straight from one change to the next.',
        replayScrub: 'Replay position',
        // Zoom on the timelines. The whole viewer is one window, so these act on every lane at once.
        replayZoomIn: 'Zoom in (Ctrl + wheel, or pinch)',
        replayZoomOut: 'Zoom out (Ctrl + wheel, or pinch)',
        replayZoomFit: 'Show the whole window',
        replayZoomFitShort: 'Fit',
        replayOverview: 'The whole window. Drag to move the view.',
        replayLaneTransitions: 'state changes',
        // After the state changes count, in amber, when chips are chosen: the legend for the amber ticks.
        replayLaneHighlighted_one: '1 into {{states}}',
        replayLaneHighlighted_other: '{{count}} into {{states}}',
        // Added to the hover text of an amber tick.
        replayTickEntered: 'entered {{states}}',
        replayLaneWrites: 'writes',
        replayBandRecorded: 'Recorded — what the plant did',
        replayBandDecided: 'Decided — what this model would have done, never sent',
        simulationSwitchReplay: 'Replay',
        simulationSwitchSimulation: 'Simulation',
        simulationModeLabel: 'Replay or simulation',
        simulationBandSimulated: 'Simulated — what the plant might have done with this model in charge',
        simulationGhost: 'Show the recording behind',
        simulationGhostTitle: 'The recording, which the simulation no longer follows from here',
        simulationShadow: 'The sidecar’s shadow: what it predicted while the recording was still the truth',
        simulationNotRead: 'not read by the model',
        simulationDiverges: 'The model first acted here · after it, the sidecar decides',
        replayLaneWouldWrite: 'would write',
        replayWouldSet: 'would set',
        replayActually: 'actually recorded',
        replayNeverChanged: 'never recorded changing in this window',
        replayDiverges: 'exact up to here · a hypothesis after it',
        replayLaneErrors: 'errors',
        replayNeverEntered: 'never entered',
        replayUnknown: 'No value recorded yet — unknown, not zero.',
        replayNothing: 'No replay to show yet.',
        replayStoryNoStart: 'The model did not start.',
        replayStoryStayed: 'Stayed in {{state}} the whole time.',
        replayStoryMoved_one: 'Changed state once, ending in {{end}}.',
        replayStoryMoved_other: 'Changed state {{count}} times, ending in {{end}}.',
        replayStoryEntered: 'went to {{states}}',
        replayStoryStateTimes_one: '{{state}} once',
        replayStoryStateTimes_other: '{{state}} {{count}} times',
        replayStoryNeverReached: 'never reached {{states}}',
        replayStoryNoWrites: 'would not have changed the machine',
        replayStoryWrites_one: 'would have changed the machine once, setting {{name}} to {{value}} at {{time}}',
        replayStoryWrites_other: 'would have changed the machine {{count}} times, first setting {{name}} to {{value}} at {{time}}',
        replayStoryStop: '.',
        replayStoryBasis_one: 'Based on {{n}} recorded reading of {{inputs}}, {{from}} to {{to}}.',
        replayStoryBasis_other: 'Based on {{n}} recorded readings of {{inputs}}, {{from}} to {{to}}.',
        replayStoryGapUntil: 'No {{input}} readings before {{time}}.',
        replayStoryGapAll: 'No {{input}} readings in this window.',
        replayStoryAfterWrite: 'After {{time}} the chart shows the machine as it really ran, not as this model would have left it.',
        replayStoryCutShort: 'Not all of the history for {{inputs}} could be loaded, so the end of the window is missing.',
        replayStoryErrors_one: 'The model hit a script error.',
        replayStoryErrors_other: 'The model hit {{count}} script errors.',
        replayStoryHung: 'The replay stopped responding partway through, so everything after that is missing.',
        replayDetails: 'Details',
        replayMore_one: '{{count}} more',
        replayMore_other: '{{count}} more',
        replayLess: 'show less',
        replayStoryFold: 'Summary',
        replayStoryFoldNotes_one: '{{count}} note',
        replayStoryFoldNotes_other: '{{count}} notes',
        // The symbol picker — which lanes are on screen. Never which lanes were replayed.
        replaySymbolsTrigger: 'Symbols · {{shown}} of {{total}}',
        replaySymbolsPanel: 'Which symbols are shown',
        replaySymbolsAll: 'All',
        replaySymbolsNone: 'None',
        replaySymbolsOnly: 'only',
        replaySymbolsOnlyTitle: 'Show only {{name}}',
        replaySymbolsGroupRecorded: 'Recorded',
        replaySymbolsGroupWrites: 'Would write',
        replaySymbolsHidden: '{{hidden}} of {{total}} symbols hidden — the summary below still describes all of them.',
        replaySymbolsShowAll: 'Show all',
        replaySymbolsNoneShown: 'No symbols shown — every lane has been put away.',
        replayLaneHide: 'Click to hide · Alt-click to show only this',
        replaySymbolsGroupSkipped: 'Not replayed',
        // Bindings the replay set aside rather than dying on. The reason itself comes from the driver.
        replaySkippedHeading_one: 'One binding could not be replayed and was left out of this chart:',
        replaySkippedHeading_other: '{{count}} bindings could not be replayed and were left out of this chart:',
        replaySkippedTryEarlier: 'Try an earlier window.',
        replaySkippedTryLater: 'Try a later window.',
        replaySkippedTryController: 'Replay against a controller that has this data, using the picker above.',
        replaySkippedTryBinding: 'Bind it to a controller symbol or a model output.',
        replaySkipIntegration: '{{path}} is an integration datapoint, and the historian never records those.',
        replaySkipNotPath: '“{{path}}” is not an entity path — a binding is controllers/…/symbols/… or models/…/variables/….',
        replaySkipNoHistory: '{{scope}} has no recorded history at all.',
        replaySkipWindowAfter: 'The window starts after {{scope}}’s recorded history ends ({{when}}).',
        replaySkipWindowBefore: 'The window ends before {{scope}}’s recorded history begins ({{when}}).',
        tileUnknown: 'Model {{id}} is not on this relay.',
        tilePick: 'Choose a model…',
        tileUnbound: 'No model chosen for this tile yet.',
        tileCommandsTitle: '{{id}} commands',
        tileStatechartTitle: '{{id}} statechart',
        // Arming — who an unattended run acts as (a consent record, not a setting).
        aiActivity: 'AI activity',
        delete: 'Delete',
        deleteHeading: 'Delete model',
        deleteMessage: 'Delete "{{name}}"?',
        deleteDetail: 'The model and its variables are removed. This cannot be undone.',
        deleteDetailRunning: 'This model is running. It will be stopped, then removed along with its variables. This cannot be undone.',
        noAiActivity: 'This model has not asked the AI for anything yet.',
        aiActivityFailed: "Could not load this model's AI activity. Try again in a moment.",
        aiOk: 'Completed',
        aiFailed: 'Failed',
        notArmed: 'Not armed',
        notArmedHint: 'Unattended runs that use tools will refuse until someone arms this model.',
        actsAsYou: 'Acts as you',
        actsAsName: 'Acts as {{name}}',
        armedHintSelf: 'When this model runs on its own, it uses tools and sends messages as you. Only you or an admin can disarm it.',
        armedHint: 'When this model runs on its own, it uses tools and sends messages as {{name}}. Only that person or an admin can disarm it.',
        armAsMe: 'Act as me',
        disarm: 'Disarm',
        armingUnavailable: 'The AI plane is not connected, so arming cannot be read here.',
        back: 'Models',
        notFound: 'Model not found.',
        hintLogic: "What is this model's logic?",
        hintState: 'What state is this model in?',
        // section headings
        status: 'Status',
        commands: 'Commands',
        // A group's machines (the model page's Machines tile).
        machines: 'Machines',
        // The toolbar names of the "Acts as" and "AI activity" tiles.
        activityTile: 'Activity',
        machinesRoot: 'root',
        machineFaulted: 'Faulted',
        machineSafe: 'If stopped: {{value}}',
        machineSafeOff: 'off',
        machineSafeOn: 'on',
        machineStopped: 'stopped',
        machinesRunning: '{{running}} of {{total}} running',
        machinesFaulted: '{{faulted}} faulted',
        machinesCount_one: '{{count}} machine',
        machinesCount_other: '{{count}} machines',
        machinesShow: 'Show each machine',
        machinesHide: 'Hide the machines',
        // The Machines tile's 2 views, and the Diagram view's labels.
        machinesDiagram: 'Diagram',
        machinesList: 'List',
        machinesGroup: 'Linked group',
        machinesInputsLabel: 'Inputs',
        machinesOutputsLabel: 'Outputs',
        machinesExposed: 'Shown in app',
        bindingWired: "set in the model's wiring; change it through the assistant",
        noMachines: 'This model is one machine, with no children.',
        noComponents: 'No components yet — they appear when toggled on from the toolbox above.',
        variables: 'Variables',
        bindings: 'Bindings',
        noInputs: 'This model declares no inputs.',
        bindingsLocked: 'Stop the model to edit its bindings.',
        unbound: 'Unbound',
        bindingNotAPath: 'Not an entity path, so the model is not subscribed to it. A binding is controllers/<id>/symbols/<path>, or models/<id>/variables/<name>.',
        unbind: 'Unbind',
        bindTo: 'Bind {{name}}',
        bindMap: 'Bind {{source}} into {{target}}',
        controllers: 'PLCs & CNCs',
        models: 'Models',
        pickSourceFirst: 'Select a source',
        aController: 'Controller symbol',
        aModel: 'Model output',
        selectController: 'Select a controller…',
        searchSymbols: 'Search symbols…',
        noSymbols: 'No matching symbols',
        selectModel: 'Select a model…',
        pickModelFirst: 'Pick a model to see its outputs',
        parameters: 'Parameters',
        statechart: 'Statechart',
        scxmlView: 'SCXML',
        cmdState: 'state by id',
        cmdEvent: 'transitions on event',
        cmdTarget: 'transitions to target',
        cmdData: 'datamodel variable',
        // status rows
        currentState: 'Current state',
        desired: 'Desired',
        outputs: 'Outputs',
        bindingsLabel: 'Bindings',
        errorLabel: 'Error',
        // lifecycle commands
        start: 'Start',
        stop: 'Stop',
        pause: 'Pause',
        resume: 'Resume',
        reset: 'Reset',
        // runtime status labels (the badge)
        noCommands: 'No commands',
        startModel: 'Start Model',
        stopModel: 'Stop Model',
        download: 'Download',
        export: 'Export data',
        // The Streaming tab: where the states and status of this model go.
        streamingTabTitle: 'Streaming to {{name}}: {{state}}. {{write}}',
        forwardsIntro: 'These Forwards write each change of the state and status of this model to a table in an outside system.',
        forwardOpen: 'Open the integration',
        forwardTable: 'Table {{table}}',
        forwardLastWrite: 'Last write {{ago}}',
        forwardNoWrite: 'No write yet',
        forwardLastError: 'Last error {{ago}}: {{error}}',
        forwardRunning: 'running',
        forwardStarting: 'starting',
        forwardError: 'error',
        forwardStopped: 'stopped',
        forwardUnreported: 'no status yet',
        // The Streaming tab: each Forward of this model, what waits and what the table has.
        streaming: 'Streaming',
        streamingBuffered: 'Buffered',
        streamingBufferedNone: 'No values wait. The Forward writes each value in about 1 s.',
        streamingBufferedSome: '{{n}} values are not in the table yet. The Forward writes them when the table takes them.',
        streamingBufferedUnknown: 'The service has not reported this Forward yet.',
        streamingSent: 'Sent',
        streamingSentNote: 'The last {{n}} values in the table, newest first. The tab reads the table again each 3 s.',
        streamingColMachine: 'Machine',
        streamingColValue: 'Value',
        streamingColValueTime: 'Value time',
        streamingColTableTime: 'In the table',
        streamingColDelay: 'Delay',
        streamingStatus: 'Model status',
        streamingReading: 'Reading the table…',
        streamingNoRows: 'The table has no values of this model yet.',
        streamingReadFailed: 'The table could not be read: {{error}}',
        streamingNoReadBack: 'This tab cannot read a {{type}} target back.',
        hintStreamingArrive: 'Do the states of this model reach the table?',
        hintStreamingError: 'Why does the Forward of this model have an error?',
        // The ghost toggle of the Statechart tile: the machines that a root state uses, as a tree under it.
        ghostTree: 'Show the machines under a state',
        // The 1 line beside the ghost toggle that says why a part of the tree is missing.
        ghostTreeEmpty: 'The state {{state}} uses no machine of the group.',
        ghostTreeFailed: 'The machines under a state could not be read.',
        ghostTreeNoCharts: 'This relay does not send the statecharts of the machines.',
        statusRunning: 'running',
        statusStopped: 'stopped',
        statusWaiting: 'waiting',
        statusStarting: 'starting',
        statusPaused: 'paused',
        statusError: 'error',
        statusUnknown: 'unknown',
        // card
        outputCount: '{{n}} outputs',
        statechartComing: 'Statechart renderer coming next',
        noControllersLinked: 'No Controllers Linked',
        notRecording: 'Not Recording Data',
        noDescription: 'No description',
        // runtime / variables / bindings / logs sections
        uptime: 'Uptime',
        startedAt: 'Started',
        notStarted: 'Not started',
        logs: 'Logs',
        bindingCount: '{{n}} bound inputs',
        noOutputs: 'No declared outputs',
        noBindings: 'No bound inputs',
        noLogs: 'No log lines yet — they stream live as the model runs.',
        inputTag: 'input',
        outputTag: 'output',
        recording: 'Data Recording',
        recordingAlwaysOn: 'Recording — always on',
        recordingScope: 'captured',
        recordingScopeValue: 'every value change · {{n}} variables',
        recordingExportSoon: 'History export & range queries — wiring next',
    },
    fleet: {
        claimTitle: 'Claim controller',
        nearbyEmpty: 'No unclaimed controllers found on your network.',
        fingerprint: 'Fingerprint',
        fieldNamePlaceholder: 'e.g. Line 3 controller',
        claim: 'Claim',
        claiming: 'Claiming…',
        cancel: 'Cancel',
        claimAdminOnly: 'Only organization admins can claim controllers.',
        claimError: 'Claim failed: {{error}}',
        detected: 'Relay {{uid}} found on your network',
    },
    notifications: {
        title: 'Notifications',
        empty: 'No notifications.',
        claimableTitle: 'New controller is ready to claim',
        dismiss: 'Dismiss',
    },
    status: {
        connected: 'connected',
        connecting: 'connecting',
        reconnecting: 'reconnecting',
        disconnected: 'disconnected',
        offline: 'offline',
        discovered: 'discovered',
        error: 'error',
        unauthorized: 'unauthorized',
    },
    discovery: {
        idle: 'Idle',
        discovering: 'Discovering',
        running: 'Discovered',
        error: 'Discovery error',
    },
    ai: {
        // The THIRD modality in the model menu. `imageMode` is the row's badge — the
        // capability — and `imageModel` is the placeholder when nothing is picked, which
        // names what the org default resolves to rather than saying 'none'.
        imageMode: 'Image',
        imageModel: 'Image model',
        // What the assistant PRODUCED — a chart, a render, an export. `alt` is the accessible
        // name when the file has none of its own, and the failed line is what a tile says
        // instead of showing a broken image, because a broken icon tells a reader nothing
        // about whether the file exists.
        'artifact.alt': 'artifact',
        'artifact.failed': 'This file could not be loaded',
        'artifact.expand': 'View full size',
        'artifact.download': 'Download',
        'artifact.close': 'Close',
        ttft: 'TTFT',
        avgLatency: 'avg',
        deleteConversation: 'Delete conversation',
        deleteFailed: 'That conversation could not be deleted — it is still in your list.',
        deleteShort: 'Delete',
        toolsMenu: 'Attach a file or choose a model',
        textMode: 'Text Mode',
        voiceMode: 'Voice Mode',
        backToConversations: 'Conversations',
        newChat: 'New chat',
        hubRecents: 'Conversations',
        opening: 'Opening…',
        thinking: 'Thinking…',
        thoughtFor: 'Thought for {{seconds}}s',
        thought: 'Thought',
        stoppedHere: 'Stopped here',
        repliedBy: 'Replied by {{name}}',
        stopRun: 'Stop this agent',
        stoppedByYou: 'Stopped by you',
        noArguments: 'No arguments',
        toolArguments: 'Arguments',
        toolResult: 'Result',
        workWriting: 'Writing…',
        workReady: 'Ready',
        workFailed: 'Failed',
        // A Python run's card (app-ui sandbox-card.ts): its folded line, its chips and its result.
        pyPython: 'Python',
        pyRunning: 'Running…',
        pyStopped: 'Stopped',
        pySeconds: '{{value}} s',
        pyDatasets_one: '{{count}} dataset',
        pyDatasets_other: '{{count}} datasets',
        pyRows_one: '{{rows}} row',
        pyRows_other: '{{rows}} rows',
        pyCutRows: 'cut at the row cap',
        pyCutTime: 'cut at the load time limit',
        pyCutHistorian: 'cut by the historian',
        pyPrintedFirst: 'Printed before the error',
        pyPrintedBeforeStop: 'Printed before it was stopped',
        pyLastExpression: 'Last expression',
        pyWarnings: 'Warnings',
        pyNothing: 'It ran and printed nothing.',
        pyCut: 'This result was too long to keep in full; what is shown is its start.',
        pyTiming: 'Interpreter {{start}} · getting ready {{prepare}} · code {{code}}',
        // A PDF report's card (app-ui report-card.ts): its status and its preview.
        reportCard: 'Report',
        reportPrinting: 'Printing…',
        reportArriving: 'The PDF appears here once it is printed.',
        reportPages_one: '{{count}} page',
        reportPages_other: '{{count}} pages',
        reportOpen: 'Open full size',
        reportLoading: 'Loading the PDF…',
        reportUnavailable: 'The PDF could not be shown here; download it below.',
        askedYou: 'Asked you',
        youAnswered: 'You answered',
        awaitingYou: 'Waiting for you',
        noAnswer: 'You did not answer',
        viewModel: 'Show the model',
        viewCode: 'Show the source',
        askWaiting: 'Waiting',
        askAnswered: 'Answered',
        systemAskedSources: 'Asked to cite its sources',
        systemRejected: 'Answer rejected — asked to fix it',
        systemDangling: 'Told about an unfinished call',
        specialist: 'Specialist',
        specialistDefault: 'Conversation',
        thinkingEffort: 'Thinking',
        runUntitled: 'Untitled run',
        messageFrom: 'Message from {{name}}',
        teamListed: 'Listed the team',
        messagedAgent: 'Messaged {{name}}',
        broadcastSent: 'Told the whole team',
        readNotes: "Read {{name}}'s notes",
        threadCount_one: '{{count}} thread',
        threadCount_other: '{{count}} threads',
        collapseAgents: 'Hide this thread’s agents',
        expandAgents: 'Show this thread’s agents',
        noteFrom: 'Note from another agent',
        messageFromSpawner: 'Message from the agent that started this one',
        runObjective: 'Objective',
        runStale: 'Superseded',
        tokenCount: '{{n}} tok',
        runModel: 'Model',
        runSteps: '{{count}} steps',
        runOpenChat: "Open this agent's chat",
        effort_low: 'Low',
        effort_medium: 'Medium',
        effort_high: 'High',
        effort_xhigh: 'Higher',
        effort_max: 'Max',
        stop: 'Stop',
        noConversation: 'No conversation.',
        chatEmpty: 'Ask about a controller, a symbol, a model — or anything on this relay.',
        openAgent: 'Open this agent',
        waiting: 'Waiting',
        waitingFor: 'Waiting for: {{reason}}',
        cancelWait: 'Cancel this wait',
        subagent: 'subagent',
        briefFrom: 'Assigned by {{name}}',
        briefFromAgent: 'Assigned by the agent that spawned this one',
        openTree: 'Whole tree',
        effort: 'Effort',
        effortFast: 'Fast',
        effortSmart: 'Smart',
        branchingFrom: 'Branching from this message — edit it and send',
        branchFromHere: 'Branch: edit this message and answer again',
        forkCopy: 'Fork: a separate copy of this thread',
        branchVersion: 'Version {{n}} of {{total}}',
        branchPrev: 'Previous version',
        branchNext: 'Next version',
        forkFromHere: 'Start a new thread from here',
        retryTurn: 'Try again',
        chapters: 'Chapters',
        pinChapter: 'Pin as a chapter',
        readAloud: 'Read aloud',
        copyMessage: 'Copy',
        stopReading: 'Stop reading',
        errStreamSilent: 'The model stopped responding for {{seconds}} s, so the request was dropped. Try again — long answers are fine, this was a lost connection.',
        errTimeout: 'The model took longer than {{seconds}}s and the request was given up on. Try again, or ask for something smaller.',
        errRateLimited: 'The provider is rate-limiting us right now. Waiting a moment and trying again usually clears it.',
        errAuth: 'Something in this request could not authenticate — it may be the model provider, your session, or the relay a tool was reaching. The original message: {{detail}}',
        errNoProviderKey: "This deployment has no API key for that model's provider, so it cannot be called. Adding the provider's key fixes it; changing model permissions will not.",
        errUpstream: 'The model provider is having trouble. Nothing was charged — trying again is safe.',
        errBatchOnlyModel: "{{model}} only takes batch jobs, so it can't answer a chat. Pick {{twin}} instead — it's the same model, served for chat.",
        errModelNotForChat: "{{model}} can't be used for chat. Pick a different model and try again.",
        errDetails: 'Details',
        cmdListLabel: 'Commands',
        cmdDesc_new: 'Start a new conversation',
        cmdDesc_stop: 'Stop the answer that is running',
        cmdDesc_retry: 'Run the last answer again',
        cmdDesc_fork: 'Copy this thread into a new conversation',
        cmdDesc_rename: 'Change the title of this thread',
        cmdDesc_model: 'Change the model for the next turns',
        cmdDesc_effort: 'Change how hard the model thinks',
        cmdDesc_specialist: 'Change the specialist that answers',
        cmdDesc_pin: 'Pin the last question as a chapter',
        cmdDesc_chapters: 'Go to a chapter',
        cmdDesc_delete: 'Delete this conversation',
        cmdDesc_help: 'Show all commands',
        cmdArg_title: 'title',
        cmdArg_model: 'model',
        cmdArg_level: 'level',
        cmdArg_specialist: 'specialist',
        cmdArg_chapter: 'chapter',
        cmdNoMatch: 'Nothing matches “{{query}}”.',
        cmdNothingToStop: 'Nothing is running.',
        cmdBusy: 'Wait for the answer to finish, or stop it with /stop.',
        cmdNothingToRetry: 'There is no answer to run again.',
        cmdNothingYet: 'This thread has no messages yet.',
        cmdRenamed: 'Renamed to “{{title}}”.',
        cmdRenameFailed: 'The title could not be changed. Try again.',
        cmdModelSet: 'Model for the next turns: {{name}}',
        cmdEffortSet: 'Thinking effort: {{level}}',
        cmdSpecialistSet: 'Specialist: {{name}}',
        cmdPinned: 'Pinned as a chapter.',
        cmdNeedsArg: 'Type a {{arg}} after /{{name}}.',
        cmdUnavailable: '/{{name}} is not available in this thread.',
        cmdDeleteConfirm: 'Delete this conversation? This cannot be undone.',
        mentionListLabel: 'Mentions',
        mentionKind_gateway: 'relay',
        mentionKind_controller: 'controller',
        mentionKind_model: 'model',
        mentionKind_dashboard: 'dashboard',
        mentionKind_widget: 'widget',
        mentionKind_specialist: 'specialist',
        talkToAgent: 'Talk to this agent',
        runCompleted: 'Completed',
        runStopped: 'Stopped',
        runRunning: 'Running',
        runAwaiting: 'Waiting',
        runQueued: 'Queued',
        // The pipes canvas — the agent tree drawn as the pipeline it is.
        pipes: 'Agent Tree',
        // The same name with its first word dropped, for a column too narrow to carry both.
        // Capitalised as two words above so the collapse is one word leaving, not a relabelling.
        pipesShort: 'Tree',
        // Badge unit words. Split from their numbers so the word can go and the number stay.
        threadsUnit_one: 'thread',
        threadsUnit_other: 'threads',
        agentsUnit_one: 'agent',
        agentsUnit_other: 'agents',
        runningUnit: 'running',
        tokensUnit: 'tok',
        trace: 'Trace',
        pipesEmpty: 'No agents have been spawned in this thread yet.',
        pipesPan: 'drag to pan',
        pipesZoomIn: 'Zoom in',
        pipesZoomOut: 'Zoom out',
        pipesFit: 'Fit to view',
        pipesTask: 'Task',
        pipesDeliv: 'Deliv',
        pipesRole: 'Role',
        closeColumn: 'Close this column',
        viewerProblems_one: '{{count}} problem',
        viewerProblems_other: '{{count}} problems',
        viewerHandOver: 'To the agent',
        viewerHandOverHint: 'Put this edit in the agent’s composer, ready to send',
        viewerHandOverPrompt: 'Here is my edited version of this widget. Please review it and use it instead of the one you returned.',
        viewerRender: 'Render',
        viewerEmpty: 'Nothing to show here.',
        openArtifact: 'Open what this agent made',
        pipesWoken: 'woken ×{{n}}',
        pipesNotMeasured: 'NOT MEASURED',
        pipesOriginHelp: 'Where this was asked for',
        notMeasuredHelp: 'These figures appear nowhere in what this agent was given or received. The answer may still be right — it was not read off anything.',
        pipesWrongSubject: 'WRONG SUBJECT',
        wrongSubjectHelp: 'This reading contradicts what the tool returned — it belongs to something else. Do not act on it.',
        pipesNoSource: 'NO SOURCE',
        noSourceHelp: 'This link was not in any search result this agent received. Do not treat it as cited.',
        pipesSources: 'SOURCES',
        sourcesHelp: 'Where each figure came from. Check that the value is reported against the thing it was read from.',
        turnWrongSubject: '{{items}} — this contradicts what the tool returned. Do not act on it.',
        turnNoSource: '{{items}} — not in any search result this answer used. Not a verified source.',
        returnDeliverable: 'Structured result',
        returnPayload: 'Returned',
        systemNote: 'Platform note',
        spawningSubagent: 'Spawning subagent…',
        spawningNamedSubagent: 'Spawning {{name}} subagent…',
        spawnedSubagent: 'Spawned subagent',
        spawnedNamedSubagent: 'Spawned {{name}} subagent',
        spawnedWorking: 'Spawned subagent — working…',
        spawnedNamedWorking: 'Spawned {{name}} subagent — working…',
        subagentReturned: '{{name}} subagent returned',
        subagentCount_one: '{{count}} agent',
        subagentCount_other: '{{count}} agents',
        runningCount_one: '{{count}} running',
        runningCount_other: '{{count}} running',
        returnSources: 'Sources',
        chatTab: 'Chat',
        navBack: 'Back',
        navForward: 'Forward',
        noRunSelected: 'No agent run selected.',
        loadOlder: 'Load earlier messages',
        transcriptEmpty: 'This agent has not said anything yet.',
        runNotStarted: 'This agent has not started yet.',
        transcriptLoading: 'Loading transcript…',
        confirmNeeded: 'Approval needed',
        confirmApprove: 'Approve',
        confirmDeny: 'Deny',
        composerPlaceholder: 'Ask anything…',
        steerPlaceholder: 'Tell the agent — it reads this at its next step…',
        agentThreadClosed: 'This agent is no longer working. Ask about its work in the chat that started it.',
        openOriginChat: 'Open that chat',
        send: 'Send',
        you: 'You',
        assistant: 'Vitesse',
        conversations: 'Conversations',
        starting: 'Starting…',
        connecting: 'Connecting…',
        unreachable: 'The AI service is unreachable.',
        newStarted: 'New conversation started.',
        resuming: 'Resuming {{id}}',
        back: 'Back',
        newConversation: 'New conversation',
        resumeConversation: 'Resume a conversation',
        loadingConversations: 'Loading…',
        noConversations: 'No conversations here yet. Threads stay in the app they were started in.',
        noConversationsMatch: 'Nothing matched that search.',
        clearSearch: 'Clear',
        searchConversations: 'Search conversations…',
        colTitle: 'Title',
        colMessages: 'Messages',
        colTokensIn: 'Tokens in',
        colTokensOut: 'Tokens out',
        colDate: 'Date',
        colTime: 'Time',
        peopleCount: '{{n}} people',
        messageCount_one: '{{count}} message',
        messageCount_other: '{{count}} messages',
        justNow: 'just now',
        minutesAgo: '{{n}}m ago',
        hoursAgo: '{{n}}h ago',
        daysAgo: '{{n}}d ago',
        resize: 'Drag to resize',
        title: 'AI Assistant',
        hubTitle: 'Conversations, memories and the console',
        composerSub: 'Ask about anything here — attach a file, or just talk.',
        chatModel: 'Chat model',
        voiceModel: 'Voice model',
        chatModelTitle: 'Chat model — this conversation only',
        voiceModelTitle: 'Voice model — this conversation only',
        add: 'Add',
        attach: 'Attach a file',
        talk: 'Talk',
        askPlaceholder: 'Type your answer…',
        startVoice: 'Start voice',
        removeFile: 'Remove',
        rejectType: '{{name}} — not a supported file type.',
        rejectSize: '{{name}} — larger than 10 MB.',
        hubMemories: 'Memories',
        hubMemoriesSub: 'What Vitesse remembers',
        hubConsole: 'AI console',
        hubConsoleSub: 'Usage, audits and settings',
        greet: {
            morning1: 'Good morning, {{name}}',
            morning1Anon: 'Good morning',
            morning2: 'Morning, {{name}}',
            morning2Anon: 'Morning',
            afternoon1: 'Good afternoon, {{name}}',
            afternoon1Anon: 'Good afternoon',
            afternoon2: 'Afternoon, {{name}}',
            afternoon2Anon: 'Afternoon',
            evening1: 'Good evening, {{name}}',
            evening1Anon: 'Good evening',
            evening2: 'Evening, {{name}}',
            evening2Anon: 'Evening',
            night1: 'Still up, {{name}}?',
            night1Anon: 'Still up?',
            night2: 'Working late, {{name}}?',
            night2Anon: 'Working late?',
            any1: 'Hi, {{name}}',
            any1Anon: 'Hi there',
            any2: 'Hello, {{name}}',
            any2Anon: 'Hello',
            any3: 'Welcome back, {{name}}',
            any3Anon: 'Welcome back',
            any4: 'Ready when you are, {{name}}',
            any4Anon: 'Ready when you are',
            any5: 'What can I help with, {{name}}?',
            any5Anon: 'What can I help with?',
            any6: 'Where should we start, {{name}}?',
            any6Anon: 'Where should we start?',
            any7: "Let's get to work, {{name}}",
            any7Anon: "Let's get to work",
            any8: 'Good to see you, {{name}}',
            any8Anon: 'Good to see you',
            any9: 'What are we looking at, {{name}}?',
            any9Anon: 'What are we looking at?',
            any10: 'Ask me anything, {{name}}',
            any10Anon: 'Ask me anything',
            any11: "What's on your mind, {{name}}?",
            any11Anon: "What's on your mind?",
            any12: 'How can I help, {{name}}?',
            any12Anon: 'How can I help?',
        },
    },
    grid: {
        colName: 'Name',
        colLabel: 'Label',
        colType: 'Type',
        colAccess: 'Access',
        colValue: 'Value',
        colChart: 'Chart',
        colActions: 'Actions',
        chartSettings: 'Chart settings',
        selectAll: 'Select all',
        searchPlaceholder: 'Use / for commands, compound them with |, &, !',
        noMatches: 'No matching symbols',
        noMatchesDesc: 'Nothing on this controller matches your search.',
        noSymbolsYet: 'No symbols yet',
        noSymbolsYetDesc: 'Symbols will appear here once the controller connects and discovers them.',
        addLabel: 'add label',
        selectedCount: '{{n}} selected',
        secondsAgo: '{{s}}s ago',
        ageSeconds: '{{n}}s',
        ageMinutes: '{{n}}m',
        ageHours: '{{n}}h',
        ageDays: '{{n}}d',
        lastChangedAt: 'Last changed at {{time}}',
        naValue: 'N/A',
        priorityOn: 'Priority polling ON — click to clear',
        priorityOff: 'Set priority polling',
        lockOn: 'Locked — click to unlock (allow writes)',
        lockOff: 'Unlocked — click to lock (block writes)',
        lockRefused: 'Lock refused',
        unlockRefused: 'Unlock refused',
        expandAll: 'Expand all',
        collapseAll: 'Collapse all',
        editValue: 'Edit value',
        bulkLock: 'Lock',
        bulkUnlock: 'Unlock',
        bulkPrioritize: 'Prioritize',
        bulkClearPriority: 'Clear priority',
        bulkPreparing: 'Preparing…',
        bulkProgress: '{{done}} / {{total}}',
        bulkResult: '{{ok}} ok · {{failed}} failed',
        truncatedChip: 'Showing the first {{n}} rows — narrow the search to see more',
    },
    // The windowed record grid (<vitesse-data-grid>) — table screen rows + navigator.
    dataGrid: {
        noRows: 'No rows.',
        loading: 'loading…',
        retrying: 'retrying…',
        loadedMore: '{{n}} rows loaded · scroll for more',
        rows: '{{n}} rows',
    },
    // Search command palette (the /cmd autocomplete descriptions).
    search: {
        type: 'Filter by data type',
        value: 'Filter by current value',
        label: 'Filter by label text',
        perm: 'Filter by permission',
        name: 'Filter by symbol name',
        author: 'Filter by author',
        official: 'Official (Vitesse) or community',
    },
    // Chart settings dialog (heading reuses grid.chartSettings).
    note: {
        edit: 'Edit note',
        placeholder: 'Click edit to add content…',
        editTitle: 'Edit note',
        cancel: 'Cancel',
        save: 'Save',
        normal: 'Normal text',
        heading1: 'Heading 1',
        heading2: 'Heading 2',
        heading3: 'Heading 3',
        bold: 'Bold (⌘B)',
        italic: 'Italic (⌘I)',
        underline: 'Underline (⌘U)',
        strike: 'Strikethrough',
        highlight: 'Highlight',
        inlineCode: 'Inline code',
        codeBlock: 'Code block',
        bulletList: 'Bullet list',
        orderedList: 'Numbered list',
        taskList: 'Checklist',
        alignLeft: 'Align left',
        alignCenter: 'Align centre',
        alignRight: 'Align right',
    },
    chart: {
        theme: 'Theme',
        done: 'Done',
        settings: 'Chart settings',
        timeWindow: 'Time window',
        sampleRate: 'Sample rate',
        name: 'Name',
        auto: 'Auto',
        yAxis: 'Y-axis range',
        stats: 'Statistics',
        pause: 'Pause',
        resume: 'Resume',
        ghost: 'Snapshot',
        ghosts: 'Snapshots',
        deleteAll: 'Delete all',
        save: 'Save',
        cancel: 'Cancel',
        ghostMeta: '{{window}}s window · {{n}} pts',
        settingsFor: 'Chart Settings - {{name}}',
        timeWindowHelp: 'How much historical data to display in charts',
        lastWindow: 'Last {{window}}',
        sampleRateOption: '{{rate}} - samples every {{interval}}',
        sampleRateHelp: 'How frequently to capture data points. Charts always scroll smoothly at 60fps.',
        yAxisMin: 'Y-Axis Min (Optional)',
        yAxisMax: 'Y-Axis Max (Optional)',
        yAxisHelp: 'Custom Y-axis range. Leave empty for automatic scaling.',
        chartName: 'Chart Name (Optional)',
        chartNamePlaceholder: 'Leave empty to use default name',
        chartNameHelp: 'Custom name for this chart. If left empty, will use the default name.',
        ghostSnapshots: 'Ghost Snapshots',
        ghostItem: 'Ghost {{n}}',
        ghostItemMeta: '{{time}} · {{window}} window · {{n}} pts',
        deleteGhost: 'Delete ghost',
        currentSettings: 'Current Settings',
        displayName: 'Display Name:',
        autoName: 'Auto (default name)',
        sampleRateLabel: 'Sample Rate:',
        bufferSize: 'Buffer Size:',
        bufferPoints: '{{n}} points',
        themeLabel: 'Theme:',
        saveSettings: 'Save Settings',
        captureGhost: 'Capture Ghost Snapshot',
        addThreshold: 'Add Threshold',
        cancelThreshold: 'Cancel Threshold',
        measureTool: 'Measure Tool',
        exitMeasureTool: 'Exit Measure Tool',
        showStats: 'Show statistics',
        hideStats: 'Hide statistics',
        thresholdSettings: 'Threshold Settings',
        thresholdValue: 'Threshold Value',
        thresholdDragHelp: 'Drag the handle on the chart to adjust, or type a value.',
        thresholdCondition: 'Condition',
        above: 'Above',
        below: 'Below',
        thresholdAboveHelp: 'Triggers when the value rises above the threshold.',
        thresholdBelowHelp: 'Triggers when the value falls below the threshold.',
        freezeChart: 'Freeze Chart',
        freezeOnCross: 'Freeze on cross',
        dontFreeze: "Don't freeze",
        freezeHelp: 'The chart pauses the moment the value crosses this threshold.',
        noFreezeHelp: 'The threshold is drawn for reference only.',
        delete: 'Delete',
        saveThreshold: 'Save Threshold',
        collecting: 'Collecting Data...',
        collectingHelp: 'Waiting for first sample at {{rate}}Hz',
    },
    // Symbol value editor dialog (square-pen on writable rows).
    valueEditor: {
        title: 'Edit value',
        symbol: 'Symbol',
        type: 'Type',
        value: 'Value',
        save: 'Save',
        saving: 'Saving…',
        invalidNumber: 'Enter a valid number',
        invalidJson: 'Invalid JSON',
        format: 'Format',
        formatHint: 'Re-indent this JSON (⌘⇧F)',
        jsonAt: 'line {{line}}, column {{column}}',
        invalidJsonAt: 'Invalid JSON at line {{line}}, column {{column}} — {{message}}',
        writeFailed: 'Write failed',
    },
    // Chart theme names (the 6 selectable palettes).
    chartTheme: {
        frost: 'Frost',
        peach: 'Peach',
        citrus: 'Citrus',
        candy: 'Candy',
        mint: 'Mint',
        berry: 'Berry',
    },
    // Logical data types shown in the symbol grid's Type column.
    dataType: {
        boolean: 'Boolean',
        integer: 'Integer',
        number: 'Number',
        string: 'String',
        object: 'Object',
        array: 'Array',
    },
    // Permission values shown in the Access column.
    access: {
        read: 'Read',
        write: 'Write',
        readWrite: 'Read & Write',
    },
    // Billing — the storage figure and what it costs. Rendered by <vitesse-billing-panel> inside
    // Clerk's organisation-profile modal, so the wording is read by people who are about to be charged.
    account: {
        title: 'Account',
        signedIn: 'Signed in',
        signOut: 'Sign out',
    },
    org: {
        title: 'Organization',
        manage: 'Manage organization',
        singleOrgOnly: 'Only your active organization is available here.',
    },
    audit: {
        kind: {
            user_profile_changed: '{{name}} updated their profile',
            member_role_changed: "{{name}}'s role changed",
            rbac_org_roles_changed: 'Access roles updated',
            rbac_user_grants_changed: "{{name}}'s permissions changed",
            org_created: 'Organization created',
            org_renamed: 'Organization renamed to {{name}}',
            organization_created: 'Organization created',
            organization_updated: 'Organization details changed',
            member_invited: '{{name}} was invited',
            member_joined: '{{name}} joined',
            member_left: '{{name}} left',
            user_updated: 'Profile updated',
            seats_changed: 'Seats changed',
            plan_upgraded: 'Plan upgraded',
            plan_downgraded: 'Plan downgraded',
            plan_canceled: 'Plan cancelled',
            payment_failed: 'Payment failed',
            credit_granted: 'Credits granted',
        },
        title: 'Audit log',
        loading: 'Loading the audit log…',
        empty: 'Nothing recorded yet.',
        viewHint: 'Everything that changed in this organization, filtered by what it touched.',
        emptyView: 'Nothing in this view yet.',
        // The view pills over the log.
        viewAll: 'All',
        viewBilling: 'Billing',
        viewAccess: 'Access',
        viewOrg: 'Organization',
        // An event with no actor was observed rather than caused. Naming that
        // explicitly beats an empty cell, which reads as missing data.
        bySystem: 'System',
        // A subject we cannot name: a departed member, or an id the roster does not
        // carry. Saying so beats printing the identifier, which reads as a bug.
        subjectUnknown: 'Someone no longer in this organization',
        actorUnknownProvider: 'Not recorded — the identity provider does not send who invited',
        actorUnknown: 'Actor not recorded',
    },
    perm: {
        /*
         * PLAIN-LANGUAGE COPY FOR THE PERMISSION EDITOR.
         *
         * The permission KEY is the wire contract and never changes; what a reader
         * sees is decided here. `label` names the action, `desc` says in a few words
         * what granting it lets a person DO — a plant manager reading this row must
         * not have to know that `symbol:write` means writing to a live PLC.
         *
         * Sub-keys are the catalog key with ':' replaced by '_', because i18next reads
         * a colon as a namespace separator.
         */
        group: {
            ai: 'AI',
            audit: 'Audit',
            billing: 'Billing',
            camera: 'Cameras',
            controller: 'Controllers',
            dashboard: 'Dashboards',
            widget: 'Widgets',
            gateway: 'Relays',
            history: 'History',
            integration: 'Integrations',
            marketplace: 'Marketplace',
            member: 'People',
            model: 'Models',
            network: 'Network',
            plugin: 'Plugins',
            program: 'Programs',
            symbol: 'Symbols',
        },
        label: {
            ai_agent_broadcast: 'Message the whole team',
            ai_agent_wake: 'Wait and resume unattended',
            ai_sandbox_run: 'Run code',
            ai_image_generate: 'Generate images',
            ai_agent_discover: 'See the other agents at work',
            ai_agent_message: 'Message another agent',
            ai_agent_read: "Read another agent's notes",
            ai_audit_view: 'View AI activity log',
            ai_config: 'Change the shared AI setup',
            ai_config_view: 'View the shared AI setup',
            ai_export: 'Export AI data',
            ai_mcp_connect: 'Connect a personal account',
            ai_memory_view: 'View AI memories',
            ai_memory_write: 'Remember facts for the AI',
            ai_finding_view: 'View AI findings',
            ai_finding_write: 'Record findings for the AI',
            ai_finding_review: 'Decide on AI proposals',
            ai_report_render: 'Make PDF reports',
            ai_purge: 'Delete AI history',
            ai_secret: 'AI provider keys',
            ai_specialist_spawn: 'Delegate to a specialist',
            ai_transcript_view: "Read everyone's conversations",
            ai_transcript_view_own: 'Read their own conversations',
            ai_use: 'Talk to the AI',
            audit_read: 'Read the audit log',
            billing_manage: 'Manage billing',
            billing_usage_view: 'View usage',
            billing_view: 'View billing',
            camera_publish: 'Allow an outside camera',
            camera_record: 'Record video',
            camera_view: 'See cameras',
            camera_watch: 'Watch cameras',
            controller_annotate: 'Label controllers',
            controller_create: 'Add a controller',
            controller_delete: 'Remove a controller',
            controller_update: 'Repoint a controller',
            controller_view: 'See controllers',
            dashboard_create: 'Create dashboards',
            dashboard_delete: 'Delete dashboards',
            dashboard_share: 'Share dashboards',
            widget_author: 'Author widgets',
            widget_thirdparty: 'Third-party widgets',
            gateway_claim: 'Claim a relay',
            gateway_remove: 'Remove a relay',
            gateway_rename: 'Rename relays',
            gateway_unclaim: 'Release a relay',
            gateway_update: 'Install relay updates',
            gateway_view: 'See relays',
            history_read: 'Read history',
            integration_browse: 'Browse integration contents',
            integration_create: 'Add an integration',
            integration_delete: 'Remove an integration',
            integration_read: 'Read integration data',
            integration_view: 'See integrations',
            integration_write: 'Write to integrations',
            member_manage: 'Manage people',
            marketplace_install: 'Install from the marketplace',
            marketplace_publish: 'Publish to the marketplace',
            model_arm: 'Let models act on their behalf',
            model_create: 'Create models',
            model_delete: 'Delete models',
            model_edit: 'Edit models',
            model_execute: 'Run models',
            model_source_view: 'Read model logic',
            model_view: 'See models',
            network_config: 'Change network settings',
            network_reset: 'Factory-reset a relay',
            network_secure: 'Change security settings',
            network_view: 'See network settings',
            plugin_view: 'See plugins',
            program_source_view: 'Read PLC program code',
            program_view: 'See PLC programs',
            symbol_annotate: 'Label values',
            symbol_config: 'Add or remove values',
            symbol_lock: 'Lock values',
            symbol_priority: 'Prioritize values',
            symbol_view: 'See values',
            symbol_write: 'Change a value',
        },
        desc: {
            ai_agent_broadcast: 'Say the same thing to every agent working alongside this one at once, which costs each of them a step',
            ai_agent_wake: 'Pause until later — a set time, or the data being ready — and pick the work back up with nobody watching. Waiting costs nothing; what the agent does when it wakes is still limited by its role',
            ai_sandbox_run: 'Run Python in a sandbox with no network, over data this agent could already read — for questions that need more rows than anyone can read',
            ai_image_generate: 'Make pictures with an image model — dashboard backgrounds and illustrations. Each one costs credit, so this is a separate decision from reading data',
            ai_agent_discover: 'Find out which agents are working alongside this one, what kind each is and what it was asked to do',
            ai_agent_message: 'Say something to an agent that is still working; it arrives at that agent’s next step, signed by the sender',
            ai_agent_read: 'Read what an agent on the same team found — what it looked at, what its tools returned and what it concluded',
            ai_audit_view: 'See what the AI did, and when',
            ai_config: "Set the organization's default AI models, prompts and tools",
            ai_config_view: "See the organization's default AI setup",
            ai_export: "Download the organization's AI conversations and memories in bulk",
            ai_mcp_connect: 'Sign in to an outside tool for their own chats',
            ai_memory_view: 'See what the AI remembers about them and about the organization',
            ai_memory_write: 'Let the AI keep a fact it learns — a preference, what a machine is, how the plant is laid out — for future conversations',
            ai_finding_view: 'See the findings log: what the AI measured, derived or proposed, when, on what evidence, and where each proposal stands',
            ai_finding_write: 'Let the AI add an entry to the findings log — a baseline, a change, an anomaly, a proposal or a KPI definition. Entries are never edited or deleted',
            ai_finding_review: "Accept, close or decline the AI's proposals in the findings log, with a reason that stays in their history",
            ai_report_render: 'Let the AI lay out a PDF report from its analysis and save it to the conversation, to keep or send',
            ai_purge: "Permanently delete AI history, including the organization's shared knowledge",
            ai_secret: 'Store or replace AI provider keys',
            ai_specialist_spawn: 'Hand work to another specialist, which then runs under its own role',
            ai_transcript_view: "Read anyone's AI conversations in this organization",
            ai_transcript_view_own: "Read their own AI conversations, and nobody else's",
            ai_use: 'Talk to the AI',
            audit_read: 'See who did what, and when',
            billing_manage: 'Change the plan and payment details',
            billing_usage_view: 'See how much the organization consumes',
            billing_view: 'See the plan and invoices',
            camera_publish: 'Give an outside camera a token to send video in',
            camera_record: 'Start and stop camera recording',
            camera_view: 'See which cameras exist',
            camera_watch: 'Watch live and recorded video',
            controller_annotate: 'Rename controllers and edit their notes',
            controller_create: 'Connect a new PLC to Vitesse',
            controller_delete: 'Disconnect a PLC from Vitesse',
            controller_update: "Change a live PLC's connection settings",
            controller_view: 'See the PLCs on this site',
            dashboard_create: 'Add a dashboard for the organization',
            dashboard_delete: "Permanently delete any of the organization's dashboards",
            dashboard_share: 'Hand a dashboard out by link or invitation, and revoke one that is out',
            widget_author: 'Write dashboard widgets the organization can place on any of its boards',
            widget_thirdparty: "See and place widgets that other organisations published to the marketplace, beyond this organisation's own and Vitesse's.",
            gateway_claim: 'Bring a new relay into this organization',
            gateway_remove: 'Permanently delete a relay record',
            gateway_rename: "Change a relay's display name",
            gateway_unclaim: 'Hand a relay back out of this organization',
            gateway_update: 'Download and install relay software updates',
            gateway_view: 'See the relays this organization owns',
            history_read: 'Look back at recorded plant data',
            integration_browse: 'Explore what a connected system holds',
            integration_create: 'Connect and configure an outside system',
            integration_delete: 'Disconnect an outside system',
            integration_read: 'Read data out of a connected system',
            integration_view: 'See which outside systems are connected',
            integration_write: 'Send data into a connected system',
            member_manage: 'Invite people, remove them, change their role',
            marketplace_install: 'Bring a shared model or widget into this organization',
            marketplace_publish: 'Offer a model or widget to every other organization',
            model_arm: 'Let a model act unattended on their behalf',
            model_create: 'Add a new model',
            model_delete: 'Permanently remove a model',
            model_edit: 'Change what a model does',
            model_execute: 'Start and stop a model',
            model_source_view: 'Read the steps inside a model',
            model_view: 'See which models exist',
            network_config: 'Change relay network and plant segments',
            network_reset: 'Erase a relay back to factory state',
            network_secure: 'Change relay security and unlock segments',
            network_view: 'See how the relay is connected',
            plugin_view: 'See which protocol plugins are running',
            program_source_view: 'Read the code inside a PLC program',
            program_view: 'See which programs a PLC runs',
            symbol_annotate: 'Rename values and edit their notes',
            symbol_config: 'Add and delete hand-configured values',
            symbol_lock: 'Block changes to a value',
            symbol_priority: 'Pin values for faster live updates',
            symbol_view: "Browse a controller's live values",
            symbol_write: 'Change a live value on the plant',
        },
        title: 'RBAC',
        pick: 'Select a member to view or change what they may do.',
        noMembers: 'No members in this organization yet.',
        filter: 'Filter permissions…',
        save: 'Save',
        saving: 'Saving…',
        // The two "empty" states read differently on purpose: one defers to the role,
        // the other overrules it.
        defaultAllowed: 'Default — allowed',
        defaultDenied: 'Default — admin only',
        granted: 'Granted',
        none: 'Revoked',
        instanceToggle: 'Show the {{count}} specialists this covers',
        instanceOf: 'Narrows “{{permission}}” to this one',
        instanceOverrides: 'Set here to override the wildcard above',
        hintMemberDefault: 'Usually everyone',
        hintAdminOnly: 'Usually admins only',
        tabPeople: 'People',
        tabRoles: 'Roles',
        roleLabel: 'Role',
        // The TRIGGER form. `roleNone` is the sentence that explains the state; a
        // table cell needs the noun, and the sentence overflowed into the next column.
        roleNoneShort: 'No role',
        roleHint: 'A role grants a bundle; the rows below adjust it for this person only.',
        overrideOf: 'From role: {{value}}',
        newRole: 'New role',
        roleName: 'Role name',
        roleNameHint: 'People holding it see this name.',
        deleteRole: 'Delete role',
        noRoles: 'No roles defined yet. Create one to grant a bundle of permissions to several people at once.',
        addRole: 'Add role',
        pickRole: 'Select a role to edit what it grants.',
        stale: 'References a permission that no longer exists',
        rolesSaved: 'Roles saved',
        duplicateRole: 'A role with that name already exists',
        emptyName: 'A role needs a name',
        lockedNote: 'Admins only. It cannot be granted to anyone else.',
        usedBy_one: '{{count}} member',
        usedBy_other: '{{count}} members',
        usedByNone: 'Not assigned to anyone',
        noMatches: 'No permission matches that filter.',
        groupCount_one: '{{count}} permission',
        groupCount_other: '{{count}} permissions',
        groupChanged: '{{changed}} of {{count}} changed',
        colRole: 'Role',
        edit: 'Edit',
        changes_one: '{{count}} change',
        changes_other: '{{count}} changes',
        searchMembers: 'Search members…',
        searchRoles: 'Search roles…',
        everyone: 'EVERYONE IN THIS ORGANIZATION',
        memberCount_one: '{{count}} member',
        memberCount_other: '{{count}} members',
        oneRole: 'One role per person. A role sets what someone may do; Edit adjusts one person within whichever they hold.',
        orgAdmin: 'Admin',
        orgMember: 'Member',
        rolesHint: 'Vitesse roles ship with the platform; your own are named permission bundles you assign to several people at once.',
        vitesseRoles: 'Vitesse roles',
        orgRoles: 'Your roles',
        forked: 'Customised',
        roleAbout: {
            Conversational_Agent: 'The agent a person talks to. Answers directly, and hands specialist work to the others.',
            SCXML_Author: 'Writes and edits statecharts. Authors them; never starts them.',
            PLC_Program_Analyst: 'Reads PLC programs — the only role that can — and the history behind them.',
            Dashboard_Designer: 'Composes dashboards. Places widgets and arranges boards; writes nothing to the plant.',
            Widget_Author: 'Writes a widget when no built-in one fits. Cannot place it on a board.',
            Data_Scientist: 'Runs code over data it was given. Returns numbers and charts, and changes nothing.',
            Dream_Agent: 'The background agent — reads widely, writes values, runs models.',
            Model_Invoked_Agent: 'Called from a model. Does what that model already can.',
            Dashboard_Invoked_Agent: 'Called from a widget. Does what that widget already can.',
        },
        roleBaseline: 'Sets what this person may do by default.',
        adminHint: 'Everything, including managing members and billing.',
        memberHint: 'The everyday baseline: view, and the actions members hold by default.',
        builtIn: 'Built in',
    },
    billing: {
        dataSplit: '{{files}} in files · {{rest}} in memory, knowledge and audits',
        addCardFailed: 'Could not start adding a card. Try again.',
        removeCardFailed: 'Could not remove that card. Try again.',
        paymentMethods: 'Payment methods',
        viewPlanHint: 'Your plan, and every plan side by side. Prices are per relay, per month.',
        viewPaymentHint: 'Cards on file. Automatic top-up charges the default one.',
        viewCreditsHint: 'Prepaid AI credit — buy, top up automatically, and cap the automation.',
        viewStorageHint: 'Everything this organization stores, and what a period of it costs.',
        relaysLabel: 'Relays',
        entConversations: 'Conversations',
        entKnowledge: 'Knowledge',
        entAudits: 'Audits',
        entAiConfig: 'AI config',
        entFiles: 'Files',
        storagePerMo: '{{amount}} / mo',
        storedLabel: '{{amount}} stored',
        storageIncluded: 'Included',
        storageBreakdownTitle: 'By type',
        relayOne: 'Relay',
        upgrade: 'Upgrade',
        downgrade: 'Downgrade',
        cancelPlan: 'Cancel plan',
        currentPlanBadge: 'Current',
        cancelPlanConfirm: 'Return to the Free plan? Paid features stop at the end of the current billing period.',
        cancelPlanFailed: 'Could not cancel the plan. Try again in a moment.',
        freeTelemetry: 'AI usage',
        perMonthPerRelay: '/mo/relay',
        storageOverageNote: 'Org storage covers conversations, AI usage, audit history and files. Each plan includes an allowance; beyond it storage is metered per GB-month — on the free plan, from the first byte.',
        integrationConnections: 'Integration connections',
        integrationConnectionsHelp: 'External systems this plan can connect, across databases, brokers and APIs.',
        hoursShort: '{{n}} h',
        daysShort: '{{n}} d',
        saveCard: 'Save card',
        savingCard: 'Saving…',
        addCard: 'Add a card',
        noCard: 'No card on file. Add one so automatic top-up has something to charge.',
        defaultCard: 'Default',
        removeCard: 'Remove',
        customAmount: 'Other',
        buyCustom: 'Buy',
        whereStored: 'Where it is stored',
        autoTopupSaveFailed: 'Could not save that setting. Try again.',
        autoTopup: 'Top up automatically',
        autoTopupWhen: 'When credit falls below $',
        autoTopupAdd: 'add $',
        autoTopupFailing: 'Automatic top-up is paused — the last few payments were declined. Check the card on file, then re-enable it.',
        plans: 'Plans',
        // The four views of the billing page, on the top-of-page pill picker.
        viewPlan: 'Plan',
        viewPayment: 'Payment',
        viewCredits: 'AI credits',
        viewStorage: 'Storage',
        compare: 'Compare plans',
        hideComparison: 'Hide comparison',
        plansUnavailable: 'Plans could not be loaded.',
        noPlanPrompt: 'Choose a plan to get started.',
        currentPlan: 'Current plan',
        choose: 'Choose',
        working: 'Working…',
        perMonth: '/mo',
        free: 'Free',
        perRelaySuffix: 'per relay',
        tierChangeFailed: 'Could not start the plan change. Try again.',
        title: 'Billing',
        loadFailed: 'Could not load your usage. Try again in a moment.',
        loading: 'Loading usage…',
        storage: 'Org data storage',
        objects: '{{count}} files',
        uncapped: 'No storage limit on this plan',
        usedOf: '{{used}} of {{total}} included',
        nearLimit: "You're close to the storage included in your plan.",
        overCap: 'You have used all the storage included in your plan. New uploads are refused until you free space or move up a plan.',
        usageHistory: 'Usage history',
        gbMonthsUnit: 'GB-months',
        inProgress: 'in progress',
        budgetTitle: 'Monthly auto top-up limit',
        budgetNone: 'No monthly limit',
        budgetOf: '{{spent}} charged of {{limit}}',
        budgetOverridden: 'You set this limit.',
        budgetFromPlan: 'Default: {{multiple}}× your plan’s monthly price. Change it any time.',
        budgetOn: 'On',
        budgetOff: 'Off',
        budgetOffNote: 'Auto top-up can charge without a monthly ceiling.',
        budgetManualUnaffected: 'This limits automatic charges only. You can always buy credits yourself, and spend the credits you already have.',
        budgetLabel: 'Monthly limit (USD)',
        budgetSave: 'Save limit',
        budgetUsePlan: 'Use plan default',
        budgetSaveFailed: 'Could not save that limit. Try again.',
        overMetered: 'You are over the storage included in your plan. The extra is billed per GB-month.',
        thisPeriod: 'This period',
        gbMonthsUsed: '{{n}} GB-months of storage used',
        overage: '{{n}} GB-months over — {{cost}}',
        withinPlan: 'Within the storage your plan includes.',
        rate: '{{price}} per GB-month beyond your plan.',
        // Plan — what the org bought. Relay count is shown because every per-relay allowance below is
        // already multiplied by it, and a number with no explanation reads as a mistake.
        plan: 'Plan',
        relays: '{{count}} relays',
        oneRelay: '1 relay',
        perRelay: '{{n}} per relay',
        unlimited: 'Unlimited',
        included: 'Included',
        notIncluded: 'Not included',
        users: 'Users',
        dashboards: 'Dashboards',
        bandwidth: 'Monthly bandwidth',
        // Credits — a prepaid balance, not an allowance the plan refills. The distinction matters
        // enough to say out loud: a customer who expects a monthly reset will run out mid-shift.
        credits: 'AI credits',
        creditsAvailable: '{{amount}} available',
        creditsHeld: '{{amount}} reserved by work in progress',
        creditsPrepaid: 'Credits are prepaid and do not expire. They are not refilled monthly.',
        creditsSuspended: 'Credit spending is suspended for this organization.',
        buyCredits: 'Buy credits',
        buying: 'Opening checkout…',
        buyFailed: 'Could not start checkout. Try again in a moment.',
        cancelCheckout: 'Cancel',
    },
};
//# sourceMappingURL=en.js.map